import { ConfigProvider } from "antd";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { RouteName } from "constants/route";
import Login from "pages/Auth/Login";
import ForgotPassword from "pages/Auth/ForgotPassword";
import { NotificationContextProvider } from "contexts/NotificationContext";
import MainLayout from "layouts/MainLayout";
import Account from "pages/AccountPages/Account";
import { DataLoadingContextProvider } from "contexts/DataLoadingContext";
import CreateAccount from "pages/AccountPages/CreateAccount";
import EditAccount from "pages/AccountPages/EditAccount";
import AccountDetail from "pages/AccountPages/AccountDetail";
import FleetModel from "pages/FleetModelPages/FleetModel";
import CreateFleetModel from "pages/FleetModelPages/CreateFleetModel";
import EditFleetModel from "pages/FleetModelPages/EditFleetModel";
import FleetModelDetail from "pages/FleetModelPages/FleetModelDetail";
import City from "pages/CityPages/City";
import CreateCity from "pages/CityPages/CreateCity";
import EditCity from "pages/CityPages/EditCity";
import Driver from "pages/DriverPages/Driver";
import CreateDriver from "pages/DriverPages/CreateDriver";
import EditDriver from "pages/DriverPages/EditDriver";
import DriverDetail from "pages/DriverPages/DriverDetail";
import Fleet from "pages/FleetPages/Fleet";
import EditFleet from "pages/FleetPages/EditFleet";
import FleetDetail from "pages/FleetPages/FleetDetail";
import EditDriverWallet from "pages/DriverWalletPages/EditDriverWallet";
import DriverWallet from "pages/DriverWalletPages/DriverWallet";
import PassengerWallet from "pages/PassengerWalletPages/PassengerWallet";
import PassengerWalletDetail from "pages/PassengerWalletPages/PassengerWalletDetail";
import Finances from "pages/Finances";
import Trip from "pages/TripPages/Trip";
import Settings from "pages/SettingPages";
import PromoCodes from "pages/PromoCodePages";
import NewPromo from "pages/PromoCodePages/CreatePromoCode";
import Passenger from "pages/PassengerPages";
import TripDetail from "pages/TripPages/TripDetail";
import NewBroadcast from "pages/BroadcastPages/NewBroadcast";
import News from "pages/BroadcastPages/News";
import CreateNews from "pages/BroadcastPages/CreateNews";
import Ratings from "pages/Ratings";
import DispatchPage from "pages/DispatchPage/Dispatch";
import DispatchDashboard from "pages/DispatchPage/Dashboard";
import PassengerDetailPage from "pages/PassengerPages/PassengerDetail";
import SosTracking from "pages/SosTracking";
import SuperAdminGuard from "components/Guard/SuperAdminGuard";
import AdminGuard from "components/Guard/AdminGuard";
import DispatchGuard from "components/Guard/DispatchGuard";
import FinanceGuard from "components/Guard/FinanceGuard";
import DispatchAndFinanceGuard from "components/Guard/DispatchAndFinanceGuard";
import TrackDriver from "pages/TrackDriver";
import TrackPassenger from "pages/TrackPassenger";
import TrackSchoolCarpooling from "pages/TrackSchoolCarpooling";
import { useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import { ERole } from "models/account";
import DriverWalletDetail from "pages/DriverWalletPages/DriverWalletDetail";
import TripRequest from "pages/TripRequestPage/RequestTrip";
import ReportRequest from "pages/ReportRequestPage/ReportRequest";
import ChapaPaymentSuccess from "pages/ChapaPaymentSuccess";
import AppVersion from "pages/AppVersionPage/AppVersion";
import CreateAppVersion from "pages/AppVersionPage/CreateAppVersion";
import AccountFinance from "pages/AccountPages/AccountFinance";
import OnlyFinanceGuard from "components/Guard/OnlyFinanceGuard";
import FinanceAccountDashboard from "pages/FinanceAccountDashboard";
import Sos from "pages/SosPage/Sos";
import SosDetail from "pages/SosPage/SosDetail";
import DispatchAndSuperAdminGuard from "components/Guard/DispatchAndSuperAdminGuard";
import { SocketIoContextProvider } from "contexts/SocketIoContext";
import Faq from "pages/FaqPages/Faq";
import CreateFaq from "pages/FaqPages/CreateFaq";
import EditFaq from "pages/FaqPages/EditFaq";
import ChangePassword from "pages/ChangePassword";
import TripRoute from "pages/TripPages/TripRoute";
import TripEstimationRoute from "pages/TripPages/TripEstimationRoute";
import DriverQrcode from "pages/DriverPages/DriverQrcode";
import Place from "pages/PlacePage/Place";
import CreatePlace from "pages/PlacePage/CreatePlace";
// import CarpoolingDetail from "pages/CarpoolingPages/CarpoolingDetail";
// import CarpoolingRoutePoints from "pages/CarpoolingPages/CarpoolingRoutePoints";
// import CarpoolingRoute from "pages/CarpoolingPages/CarpoolingRoute";
// import Carpooling from "pages/CarpoolingPages/Carpooling";
import TripRouteOld from "pages/TripPages/TripOldRoute";
import EditPlace from "pages/PlacePage/EditPlace";
import ViewPlacesOnMap from "pages/PlacePage/ViewPlacesOnMap";
import Otp from "pages/Otp";
import TripRoutePoints from "pages/TripPages/TripRoutePoints";
import FinanceWithPoliceGuard from "components/Guard/FinanceWithPoliceGuard";
import DispatchWithPolicyGuard from "components/Guard/DispatchWithPolicyGuard";
import DriverSearch from "pages/DriverPages/DriverSearch";
import DispatchFinanceAndPoliceGuard from "components/Guard/DispatchFinanceAndPoliceGuard";
import ActivityLog from "pages/ActivityLog";
import StuckDrivers from "pages/StuckDrivers";
import PenaltyManagement from "pages/PenaltyPages/Penalty";
import LotteryManagement from "pages/LotteryPages/Lottery";
import SystemHealth from "pages/SystemHealth";

function App() {
  const getRoute = useMemo(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const role = (jwtDecode(token) as any)?.role;
        if ([ERole.SUPER_ADMIN, ERole.ADMIN, ERole.DISPATCH].includes(role))
          return RouteName.DASHBOARD;
        else if (role === ERole.CASHER) return RouteName.FINANCE;
        else if (role === ERole.POLICE) return RouteName.DRIVER_SEARCH;
        else return RouteName.LOGIN;
      } catch (error) { }
    }
    return RouteName.LOGIN;
  }, []);

  return (
    <BrowserRouter>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "rgb(30,30,30)",
          },
          components: {
            Input: {
              boxShadow: "0 0 0 1px rgba(3, 3, 3, 0.3)",
              activeShadow: "0 0 0 1px rgba(3, 3, 3, 0.5)",
            },
            Select: {
              boxShadow: "0 0 0 1px rgba(3, 3, 3, 0.64)",
              boxShadowSecondary: "0 0 0 1px rgba(3, 3, 3, 0.64)",
              boxShadowTertiary: "0 0 0 1px rgba(3, 3, 3, 0.64)",
            },
          },
        }}
      >
        <NotificationContextProvider>
          <SocketIoContextProvider>
            <DataLoadingContextProvider>
              <Routes>
                <Route path={"/"} element={<Navigate to={getRoute} />} index />
                <Route path={"/home"} element={<Navigate to={getRoute} />} />
                <Route path={RouteName.LOGIN} Component={Login} index />
                <Route
                  path={RouteName.FORGOT_PASSWORD}
                  Component={ForgotPassword}
                />
                <Route path={RouteName.SOS_TRACKING} Component={SosTracking} />
                <Route path={RouteName.TRACK_DRIVER} Component={TrackDriver} />
                <Route
                  path={RouteName.TRACK_SCHOOL_CARPOOLING}
                  Component={TrackSchoolCarpooling}
                />
                <Route
                  path={RouteName.TRACK_PASSENGER}
                  Component={TrackPassenger}
                />
                <Route Component={MainLayout}>
                  {/*/////////////////////////////////// Start Accounts Routes ///////////////////////////////////*/}
                  <Route Component={SuperAdminGuard}>
                    <Route path={RouteName.ACCOUNT} Component={Account} />
                    <Route
                      path={RouteName.ACTIVITY_LOG}
                      Component={ActivityLog}
                    />
                    <Route
                      path={RouteName.CREATE_ACCOUNT}
                      Component={CreateAccount}
                    />
                    <Route
                      path={RouteName.EDIT_ACCOUNT}
                      Component={EditAccount}
                    />
                    <Route
                      path={RouteName.ACCOUNT_DETAIL}
                      Component={AccountDetail}
                    />
                    <Route
                      path={RouteName.ACCOUNT_FINANCe}
                      Component={AccountFinance}
                    />
                    <Route
                      path={RouteName.SYSTEM_HEALTH}
                      Component={SystemHealth}
                    />
                  </Route>
                  {/*/////////////////////////////////// End Accounts Routes ///////////////////////////////////*/}

                  <Route Component={AdminGuard}>
                    {/*/////////////////////////////////// Start City Route ///////////////////////////////////*/}
                    <Route path={RouteName.PLACE} Component={Place} />
                    <Route
                      path={RouteName.CREATE_PLACE}
                      Component={CreatePlace}
                    />
                    <Route path={RouteName.EDIT_PLACE} Component={EditPlace} />
                    <Route
                      path={RouteName.VIEW_PLACES_ON_MAP}
                      Component={ViewPlacesOnMap}
                    />
                    <Route path={RouteName.CITY} Component={City} />
                    <Route
                      path={RouteName.CREATE_CITY}
                      Component={CreateCity}
                    />
                    <Route path={RouteName.EDIT_CITY} Component={EditCity} />
                    {/*/////////////////////////////////// End City Routes /////////////////////////////////////*/}
                    {/*/////////////////////////////////// Start Faq Route ///////////////////////////////////*/}
                    <Route path={RouteName.FAQ} Component={Faq} />
                    <Route path={RouteName.CREATE_FAQ} Component={CreateFaq} />
                    <Route path={RouteName.EDIT_FAQ} Component={EditFaq} />
                    {/*/////////////////////////////////// End City Routes /////////////////////////////////////*/}
                    <Route Component={SuperAdminGuard}>
                      {/*/////////////////////////////////// Start Fleet Model Routes ///////////////////////////////////*/}
                      <Route
                        path={RouteName.FLEET_MODEL}
                        Component={FleetModel}
                      />
                      <Route
                        path={RouteName.CREATE_FLEET_MODEL}
                        Component={CreateFleetModel}
                      />
                      <Route
                        path={RouteName.EDIT_FLEET_MODEL}
                        Component={EditFleetModel}
                      />
                      <Route
                        path={RouteName.FLEET_MODEL_DETAIL}
                        Component={FleetModelDetail}
                      />
                      {/*/////////////////////////////////// End Fleet Model Routes ///////////////////////////////////*/}
                    </Route>
                    {/*/////////////////////////////////// Start Notification Routes ///////////////////////////////////*/}
                    <Route
                      path={RouteName.NEWS_BROADCAST}
                      Component={NewBroadcast}
                    />
                    <Route path={RouteName.NEWS} Component={News} />
                    <Route path={RouteName.NEW_NEWS} Component={CreateNews} />
                    <Route path={RouteName.NEW_PROMO} Component={NewPromo} />
                    {/*/////////////////////////////////// End Notification Routes ///////////////////////////////////*/}
                    {/*/////////////////////////////////// Start Passenger Routes ///////////////////////////////////*/}
                    <Route path={RouteName.PASSENGER} Component={Passenger} />
                    <Route
                      path={RouteName.PASSENGER_DETAIL}
                      Component={PassengerDetailPage}
                    />{" "}
                    {/*/////////////////////////////////// End Passenger Routes ///////////////////////////////////*/}
                    <Route path={RouteName.RATING} Component={Ratings} />
                    <Route Component={SuperAdminGuard}>
                      <Route path={RouteName.SETTING} Component={Settings} />
                      <Route
                        path={RouteName.APP_VERSION}
                        Component={AppVersion}
                      />
                    </Route>
                    <Route
                      path={RouteName.CREATE_APP_VERSION}
                      Component={CreateAppVersion}
                    />
                    <Route path={RouteName.OTP} Component={Otp} />
                    {/* Standalone Management Pages */}
                    <Route
                      path={RouteName.STUCK_DRIVERS}
                      Component={StuckDrivers}
                    />
                    <Route
                      path={RouteName.PENALTY_MANAGEMENT}
                      Component={PenaltyManagement}
                    />
                    <Route
                      path={RouteName.LOTTERY_MANAGEMENT}
                      Component={LotteryManagement}
                    />
                  </Route>
                  {/*/////////////////////////////////// Start Driver Routes /////////////////////////////////*/}
                  <Route Component={FinanceWithPoliceGuard}>
                    <Route
                      path={RouteName.DRIVER_SEARCH}
                      Component={DriverSearch}
                    />
                  </Route>
                  <Route Component={FinanceGuard}>
                    <Route path={RouteName.DRIVER} Component={Driver} />
                    <Route
                      path={RouteName.DRIVER_DETAIL}
                      Component={DriverDetail}
                    />
                    <Route
                      path={RouteName.CREATE_DRIVER}
                      Component={CreateDriver}
                    />
                    <Route
                      path={RouteName.EDIT_DRIVER}
                      Component={EditDriver}
                    />
                    {/*/////////////////////////////////// End Driver Routes ///////////////////////////////////*/}
                    {/*/////////////////////////////////// Start Fleet Routes ///////////////////////////////////*/}
                    <Route path={RouteName.FLEET} Component={Fleet} />
                    <Route path={RouteName.EDIT_FLEET} Component={EditFleet} />
                    <Route
                      path={RouteName.FLEET_DETAIL}
                      Component={FleetDetail}
                    />
                    {/*/////////////////////////////////// End Fleet Routes ///////////////////////////////////*/}
                    {/*/////////////////////////////////// Start Driver Wallet Routes ///////////////////////////////////*/}
                    <Route
                      path={RouteName.DRIVER_WALLET}
                      Component={DriverWallet}
                    />
                    <Route
                      path={RouteName.EDIT_DRIVER_WALLET}
                      Component={EditDriverWallet}
                    />
                    <Route
                      path={RouteName.DRIVER_WALLET_DETAIL}
                      Component={DriverWalletDetail}
                    />
                    <Route
                      path={RouteName.PASSENGER_WALLET}
                      Component={PassengerWallet}
                    />
                    <Route
                      path={RouteName.PASSENGER_WALLET_DETAIL}
                      Component={PassengerWalletDetail}
                    />
                    {/*/////////////////////////////////// End Driver Wallet Routes ///////////////////////////////////*/}
                  </Route>
                  <Route Component={DispatchWithPolicyGuard}>
                    {/*/////////////////////////////////// Start Driver Sos Routes ///////////////////////////////////*/}
                    <Route path={RouteName.SOS} Component={Sos} />
                    <Route path={RouteName.SOS_DETAIL} Component={SosDetail} />
                    {/*/////////////////////////////////// End Driver Sos Routes ///////////////////////////////////*/}
                  </Route>
                  {/*/////////////////////////////////// Start Trip Routes ///////////////////////////////////*/}
                  <Route Component={DispatchAndFinanceGuard}>
                    <Route path={RouteName.TRIP} Component={Trip} />
                    <Route
                      path={RouteName.TRIP_REQUEST}
                      Component={TripRequest}
                    />
                  </Route>
                  {/* <Route path={RouteName.CARPOOLING} Component={Carpooling} /> */}
                  <Route Component={DispatchFinanceAndPoliceGuard}>
                    <Route path={RouteName.TRIP_ROUTE} Component={TripRoute} />
                    <Route
                      path={RouteName.TRIP_ROUTE_OLD}
                      Component={TripRouteOld}
                    />
                    <Route
                      path={RouteName.TRIP_ROUTE_ESTIMATION}
                      Component={TripEstimationRoute}
                    />
                    <Route
                      path={RouteName.TRIP_ROUTE_POINTS}
                      Component={TripRoutePoints}
                    />
                    <Route
                      path={RouteName.TRIP_DETAIL}
                      Component={TripDetail}
                    />
                    {/* <Route
                      path={RouteName.CARPOOLING_ROUTE}
                      Component={CarpoolingRoute}
                    />
                    <Route
                      path={RouteName.CARPOOLING_ROUTE_POINTS}
                      Component={CarpoolingRoutePoints}
                    />
                    <Route
                      path={RouteName.CARPOOLING_DETAIL}
                      Component={CarpoolingDetail}
                    /> */}
                  </Route>
                  <Route Component={DispatchGuard}>
                    <Route Component={DispatchAndSuperAdminGuard}>
                      <Route
                        path={RouteName.DISPATCHER}
                        Component={DispatchPage}
                      />
                    </Route>
                    <Route
                      path={RouteName.DASHBOARD}
                      Component={DispatchDashboard}
                    />
                    {/*/////////////////////////////////// End Trip Routes ///////////////////////////////////*/}
                  </Route>
                  <Route Component={OnlyFinanceGuard}>
                    <Route
                      path={RouteName.FINANCE_DASHBOARD}
                      Component={FinanceAccountDashboard}
                    />
                  </Route>
                  <Route Component={FinanceGuard}>
                    <Route path={RouteName.FINANCE} Component={Finances} />
                    <Route
                      path={RouteName.REPORT_REQUEST}
                      Component={ReportRequest}
                    />
                    <Route path={RouteName.PROMO_CODE} Component={PromoCodes} />
                  </Route>
                  <Route path={"*"} element={<>Page not found</>} />
                  <Route
                    path={RouteName.CHANGE_PASSWORD}
                    Component={ChangePassword}
                  />
                </Route>
                <Route
                  path={RouteName.CHAPA_TRANSACTION_SUCCESS}
                  Component={ChapaPaymentSuccess}
                />
                <Route
                  path={RouteName.DRIVER_QRCODE}
                  Component={DriverQrcode}
                />
              </Routes>
            </DataLoadingContextProvider>
          </SocketIoContextProvider>
        </NotificationContextProvider>
      </ConfigProvider>
    </BrowserRouter>
  );
}

export default App;
